import { writable, get } from 'svelte/store';
import { toast } from 'svelte-sonner';
import type {
  NowBoardPollSeconds,
  NowDensity,
  NowItem,
  NowState,
  NowVisibleDensity,
  Task,
} from '../types';
import {
  DEFAULT_NOW_BOARD_POLL_SECONDS,
  DEFAULT_NOW_STATE,
  NOW_BOARD_POLL_SECONDS,
  NOW_DENSITIES,
  NOW_VISIBLE_DENSITIES,
} from '../types';
import { persistedWritable } from './persistedWritable';
import {
  addNowItem,
  fetchNow,
  fetchNowSuggestions,
  NowRequestError,
  removeNowItem,
  reorderNowItems,
  updateNowPreferences,
} from '../nowApi';
import { subscribeToAllCachedTasks, updateTaskInCache } from './projectTasksCache';
import { jsonHeaders, readApiErrorMessage } from '../apiHelpers';
import { undoableAction } from '../undoManager';

export const nowState = writable<NowState>({ ...DEFAULT_NOW_STATE });
export const nowDensity = persistedWritable<NowDensity>('nowDensity', 'hidden');
export const nowLastVisibleDensity = persistedWritable<NowVisibleDensity>(
  'nowLastVisibleDensity',
  'compact',
);
export const nowBoardPollSeconds = persistedWritable<NowBoardPollSeconds>(
  'nowBoardPollSeconds',
  DEFAULT_NOW_BOARD_POLL_SECONDS,
);

export function normalizeNowBoardPollSeconds(value: unknown): NowBoardPollSeconds {
  return NOW_BOARD_POLL_SECONDS.includes(value as NowBoardPollSeconds)
    ? (value as NowBoardPollSeconds)
    : DEFAULT_NOW_BOARD_POLL_SECONDS;
}

export function setNowBoardPollSeconds(seconds: NowBoardPollSeconds) {
  nowBoardPollSeconds.set(normalizeNowBoardPollSeconds(seconds));
}

const NOW_BOARD_KEEP_AWAKE_KEY = 'nowBoardKeepAwake';

function readNowBoardKeepAwake(): boolean {
  try {
    return JSON.parse(localStorage.getItem(NOW_BOARD_KEEP_AWAKE_KEY) ?? 'false') === true;
  } catch {
    return false;
  }
}

/** Per-device "Keep screen awake" choice for the Now board. Storage failures keep it in memory. */
export const nowBoardKeepAwake = writable<boolean>(readNowBoardKeepAwake());
nowBoardKeepAwake.subscribe((value) => {
  try {
    localStorage.setItem(NOW_BOARD_KEEP_AWAKE_KEY, JSON.stringify(value));
  } catch {
    /* private mode or storage disabled */
  }
});

export function setNowBoardKeepAwake(on: boolean) {
  nowBoardKeepAwake.set(on);
}

/**
 * True while the Now board is open as a desk display (keep-awake on and supported).
 * Set by the board page; the mobile tab bar hides while it is true.
 */
export const nowBoardDisplayMode = writable(false);

/** Incremented whenever the client writes Now after the last Inertia hydrate. */
let nowClientGeneration = 0;
/** Bumped on live writes so in-flight GET /now responses cannot overwrite them. */
let refreshSeq = 0;
/** Bumped by every local reorder so an older request's response cannot undo a newer order. */
let orderSeq = 0;
let orderRequestsInFlight = 0;
/** The order the server last confirmed; failed reorders roll back to it. */
let confirmedOrderIds: string[] = [];
/** Reorders go out one at a time so the server applies them in the order the user made them. */
let orderQueue: Promise<unknown> = Promise.resolve();

function commitNow(next: NowState) {
  nowClientGeneration += 1;
  nowState.set(next);
}

function setLiveNow(next: NowState) {
  refreshSeq += 1;
  commitNow(next);
}

export function initNow(state: NowState | null | undefined, options?: { force?: boolean }) {
  if (!state || options?.force) {
    nowClientGeneration = 0;
    refreshSeq = 0;
  }
  if (nowClientGeneration > 0) return;
  nowState.set(state ? { ...DEFAULT_NOW_STATE, ...state } : { ...DEFAULT_NOW_STATE });
}

export function isNowVisible(density: NowDensity = get(nowDensity)): boolean {
  return density !== 'hidden';
}

export function isNowBoardPath(url: string | undefined | null): boolean {
  if (!url) return false;
  const path = url.split('?')[0];
  return path === '/now/board';
}

export function setNowDensity(density: NowDensity) {
  const next = NOW_DENSITIES.includes(density) ? density : 'hidden';
  nowDensity.set(next);
  if (next !== 'hidden') nowLastVisibleDensity.set(next);
}

export function toggleNowVisibility() {
  if (get(nowDensity) === 'hidden') {
    const last = get(nowLastVisibleDensity);
    nowDensity.set(NOW_VISIBLE_DENSITIES.includes(last) ? last : 'compact');
    return;
  }
  nowLastVisibleDensity.set(get(nowDensity) as NowVisibleDensity);
  nowDensity.set('hidden');
}

export function isTaskInNow(taskId: string, state: NowState = get(nowState)): boolean {
  return state.items.some((item) => item.taskId === taskId);
}

function applyNowItemPatch(
  items: NowItem[],
  taskId: string,
  patch: Partial<Pick<NowItem, 'title' | 'priority' | 'status'>>,
): NowItem[] {
  return items.map((item) => (item.taskId === taskId ? { ...item, ...patch } : item));
}

/** Patch visible Now chips immediately, or refresh membership after leave/complete. */
export function patchNowTask(
  taskId: string,
  patch: {
    title?: string;
    priority?: Task['priority'];
    status?: Task['status'];
    deleted?: boolean;
  },
) {
  const state = get(nowState);
  const tracked =
    state.items.some((item) => item.taskId === taskId) ||
    state.completedToday.some((item) => item.taskId === taskId);
  if (!tracked) return;

  if (patch.deleted || (patch.status && patch.status !== 'todo')) {
    refreshNowQuietly();
    return;
  }

  const fields: Partial<Pick<NowItem, 'title' | 'priority' | 'status'>> = {};
  if (patch.title !== undefined) fields.title = patch.title;
  if (patch.priority !== undefined) fields.priority = patch.priority;
  if (patch.status !== undefined) fields.status = patch.status;
  if (Object.keys(fields).length === 0) return;

  setLiveNow({
    ...state,
    items: applyNowItemPatch(state.items, taskId, fields),
    completedToday: applyNowItemPatch(state.completedToday, taskId, fields),
  });
}

function syncNowFromCachedTasks(allTasks: Record<string, Task[]>) {
  const state = get(nowState);
  if (state.items.length === 0 && state.completedToday.length === 0) return;

  let fieldChanged = false;
  let membershipChanged = false;

  const nextItems = state.items.map((item) => {
    const projectTasks = allTasks[item.projectId];
    if (!projectTasks) return item;
    const task = projectTasks.find((entry) => entry.id === item.taskId);
    if (!task) {
      membershipChanged = true;
      return item;
    }
    if (task.status !== 'todo') membershipChanged = true;
    if (
      task.title !== item.title ||
      task.priority !== item.priority ||
      task.status !== item.status
    ) {
      fieldChanged = true;
      return { ...item, title: task.title, priority: task.priority, status: task.status };
    }
    return item;
  });

  const nextCompleted = state.completedToday.map((item) => {
    const projectTasks = allTasks[item.projectId];
    if (!projectTasks) return item;
    const task = projectTasks.find((entry) => entry.id === item.taskId);
    if (!task) return item;
    if (task.title !== item.title || task.priority !== item.priority) {
      fieldChanged = true;
      return { ...item, title: task.title, priority: task.priority };
    }
    return item;
  });

  if (fieldChanged) {
    setLiveNow({
      ...get(nowState),
      items: nextItems,
      completedToday: nextCompleted,
    });
  }
  if (membershipChanged) refreshNowQuietly();
}

let cacheUnsub: (() => void) | null = null;

export function startNowCacheSync() {
  if (cacheUnsub) return;
  cacheUnsub = subscribeToAllCachedTasks(syncNowFromCachedTasks);
}

export async function refreshNow(): Promise<NowState> {
  const seq = ++refreshSeq;
  const state = await fetchNow();
  // A GET that lands while a reorder is in flight still has the old order.
  if (seq !== refreshSeq || orderRequestsInFlight > 0) return get(nowState);
  commitNow(state);
  return state;
}

/** Poll/visibility refresh. Swallows network and auth errors so they do not become unhandled rejections. */
export function refreshNowQuietly() {
  void refreshNow().catch(() => {});
}

export async function addToNow(taskId: string): Promise<boolean> {
  const previous = get(nowState);
  if (isTaskInNow(taskId, previous)) return true;
  if (!previous.canAdd) {
    toast.error(`Now is full (${previous.capacity} slots)`);
    return false;
  }

  try {
    setLiveNow(await addNowItem(taskId));
    return true;
  } catch (error) {
    nowState.set(previous);
    toast.error(error instanceof Error ? error.message : 'Could not add to Now');
    return false;
  }
}

export async function removeFromNow(taskId: string): Promise<boolean> {
  const previous = get(nowState);
  if (!isTaskInNow(taskId, previous)) return true;

  setLiveNow({
    ...previous,
    items: previous.items.filter((item) => item.taskId !== taskId),
    occupied: Math.max(0, previous.occupied - 1),
    canAdd: true,
  });

  try {
    setLiveNow(await removeNowItem(taskId));
    return true;
  } catch (error) {
    nowState.set(previous);
    toast.error(error instanceof Error ? error.message : 'Could not remove from Now');
    return false;
  }
}

/** `items` in the order of `taskIds`, or null when the ids are not exactly the current items. */
function orderNowItems(items: NowItem[], taskIds: string[]): NowItem[] | null {
  if (taskIds.length !== items.length) return null;
  const byId = new Map(items.map((item) => [item.taskId, item]));
  const ordered = taskIds.map((id) => byId.get(id));
  if (new Set(taskIds).size !== taskIds.length || ordered.some((item) => !item)) return null;
  return ordered as NowItem[];
}

function nowItemIds(state: NowState = get(nowState)): string[] {
  return state.items.map((item) => item.taskId);
}

/** Show `taskIds` order locally. Skipped when the membership changed; the server decides then. */
function applyNowOrder(taskIds: string[]) {
  const state = get(nowState);
  const items = orderNowItems(state.items, taskIds);
  if (!items) return;
  orderSeq += 1;
  setLiveNow({ ...state, items });
}

/**
 * PATCH the order. On failure, restore the last confirmed order locally (unless a newer
 * reorder has been applied since) and rethrow. A 422 means the list was stale, so resync.
 * `baseIds` is the order before this change; with nothing in flight, it is the server's.
 */
function sendNowOrder(taskIds: string[], baseIds: string[]): Promise<void> {
  const seq = orderSeq;
  if (orderRequestsInFlight === 0) confirmedOrderIds = baseIds;
  orderRequestsInFlight += 1;
  const request = orderQueue.then(async () => {
    try {
      const next = await reorderNowItems(taskIds);
      orderRequestsInFlight -= 1;
      confirmedOrderIds = nowItemIds(next);
      if (seq === orderSeq) setLiveNow(next);
    } catch (error) {
      orderRequestsInFlight -= 1;
      if (seq === orderSeq) applyNowOrder(confirmedOrderIds);
      if (error instanceof NowRequestError && error.status === 422) refreshNowQuietly();
      throw error;
    }
  });
  orderQueue = request.catch(() => {});
  return request;
}

/** Reorder the active items (the first is Up next). Optimistic, with rollback on failure. */
export async function reorderNow(taskIds: string[]): Promise<boolean> {
  const previousIds = nowItemIds();
  applyNowOrder(taskIds);
  try {
    await sendNowOrder(taskIds, previousIds);
    return true;
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Could not reorder Now');
    return false;
  }
}

/**
 * Promote a task to Up next, with an Undo toast. Returns false (no request, no toast)
 * when the task is already first or not in Now.
 */
export function moveNowItemToTop(taskId: string): boolean {
  const previousIds = nowItemIds();
  if (previousIds.indexOf(taskId) <= 0) return false;
  const nextIds = [taskId, ...previousIds.filter((id) => id !== taskId)];
  let undoSeq = 0;

  // sendNowOrder rolls back on failure, so no onCommitError / onUndoError here.
  undoableAction({
    message: 'Moved to Up next',
    onOptimisticApply: () => applyNowOrder(nextIds),
    onCommit: () => sendNowOrder(nextIds, previousIds),
    onOptimisticUndo: () => {
      applyNowOrder(previousIds);
      undoSeq = orderSeq;
    },
    // The Undo request waits for the forward one. If the user reordered again meanwhile,
    // that newer request already carries the full order, so sending Undo after it would undo it.
    onUndo: () => (orderSeq === undoSeq ? sendNowOrder(previousIds, nextIds) : undefined),
    commitErrorMessage: 'Could not move to Up next',
    undoErrorMessage: 'Could not undo the move',
  });
  return true;
}

export async function toggleTaskInNow(task: Pick<Task, 'id' | 'status'>): Promise<boolean> {
  if (task.status !== 'todo') return false;
  return isTaskInNow(task.id) ? removeFromNow(task.id) : addToNow(task.id);
}

export async function completeNowItem(taskId: string, projectId: string): Promise<boolean> {
  const previous = get(nowState);
  const item = previous.items.find((entry) => entry.taskId === taskId);
  if (!item) return false;

  const remaining = previous.items.filter((entry) => entry.taskId !== taskId);
  const completedItem = { ...item, status: 'done' as const };
  const completedToday = previous.refillSameDay
    ? previous.completedToday
    : [...previous.completedToday, completedItem];
  const occupied = previous.refillSameDay
    ? remaining.length
    : remaining.length + completedToday.length;

  setLiveNow({
    ...previous,
    items: remaining,
    completedToday,
    occupied,
    canAdd: occupied < previous.capacity,
  });

  try {
    const response = await fetch(`/projects/${projectId}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({ task: { status: 'done' } }),
    });
    if (!response.ok) throw new Error(await readApiErrorMessage(response));
    const updated = await response.json();
    updateTaskInCache(projectId, taskId, updated);
    await refreshNow();
    return true;
  } catch (error) {
    nowState.set(previous);
    toast.error(error instanceof Error ? error.message : 'Could not complete task');
    return false;
  }
}

export async function saveNowPreferences(prefs: {
  capacity?: number;
  refillSameDay?: boolean;
}): Promise<boolean> {
  const previous = get(nowState);
  try {
    setLiveNow(await updateNowPreferences(prefs));
    return true;
  } catch (error) {
    nowState.set(previous);
    toast.error(error instanceof Error ? error.message : 'Could not update Now settings');
    return false;
  }
}

export function loadNowSuggestions() {
  return fetchNowSuggestions();
}

export function syncNowFromTaskChange(
  taskId: string,
  change: {
    title?: string;
    priority?: Task['priority'];
    status?: Task['status'];
    deleted?: boolean;
  },
) {
  patchNowTask(taskId, change);
}
